import {
  ArrowLeft,
  ArrowRight,
  Check,
  CircleCheck,
  Clock3,
  CreditCard,
  Gift,
  Hash,
  LoaderCircle,
  Search,
  UserCheck,
  Users,
  X,
} from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "../api";
import { courseGrabGrantInput } from "../course-grab";
import {
  autoDormCheckGrantFingerprint,
  buildAutoDormCheckGrantInput,
  createAutoDormCheckGrantDraft,
  validateAutoDormCheckGrantDraft,
  type AutoDormCheckGrantDraft,
} from "../auto-dorm-check-grants";
import type {
  AutoDormCheckGrantCampaign,
  AutoDormCheckGrantInput,
  AutoDormCheckGrantPreview,
  AutoDormCheckGrantScope,
  AutoDormCheckPlan,
  UserSummary,
} from "../types";
import { ActionButton, Tag } from "./design-system";
import { FilterPopover } from "./filter-popover";

const SCOPE_OPTIONS: Array<{
  value: AutoDormCheckGrantScope;
  label: string;
  description: string;
  icon: typeof Users;
}> = [
  {
    value: "all_users",
    label: "所有人",
    description: "全体当前注册用户",
    icon: Users,
  },
  {
    value: "selected_users",
    label: "指定用户",
    description: "按姓名或学号选择",
    icon: UserCheck,
  },
  {
    value: "paid_any",
    label: "所有付费用户",
    description: "有成功付款记录的用户",
    icon: CreditCard,
  },
  {
    value: "paid_plan",
    label: "指定付费套餐",
    description: "购买过某一套餐的用户",
    icon: Gift,
  },
];

export function AutoDormCheckGrantDialog(
  props: {
    open: boolean;
    plans: AutoDormCheckPlan[];
    onClose: () => void;
  } & (
    | {
        product?: "auto-dorm-check";
        onGranted: (
          campaign: AutoDormCheckGrantCampaign,
        ) => Promise<void> | void;
      }
    | {
        product: "course-grab" | "lerun";
        onGranted: () => Promise<void> | void;
      }
  ),
) {
  const { open, plans, onClose } = props;
  const isCourse = props.product === "course-grab";
  const isLerun = props.product === "lerun";
  const countOnly = isCourse || isLerun;
  const dialogRef = useRef<HTMLDialogElement>(null);
  const previewSequence = useRef(0);
  const submissionAttempt = useRef<{
    fingerprint: string;
    idempotencyKey: string;
  } | null>(null);
  const titleId = useId();
  const descriptionId = useId();
  const [draft, setDraft] = useState<AutoDormCheckGrantDraft>(() => ({
    ...createAutoDormCheckGrantDraft(),
    quotaKind: countOnly ? "count" : "time",
  }));
  const [selectedUsers, setSelectedUsers] = useState<UserSummary[]>([]);
  const [userQuery, setUserQuery] = useState("");
  const [userResults, setUserResults] = useState<UserSummary[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearchError, setUserSearchError] = useState<string | null>(null);
  const [step, setStep] = useState<"configure" | "review">("configure");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState<AutoDormCheckGrantPreview | null>(
    null,
  );
  const [reviewInput, setReviewInput] =
    useState<AutoDormCheckGrantInput | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const busy = previewLoading || submitting;

  const planByCode = useMemo(
    () => new Map(plans.map((plan) => [plan.code, plan])),
    [plans],
  );

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  useEffect(() => {
    if (open) return;
    previewSequence.current += 1;
    submissionAttempt.current = null;
    setDraft({
      ...createAutoDormCheckGrantDraft(),
      quotaKind: countOnly ? "count" : "time",
    });
    setSelectedUsers([]);
    setUserQuery("");
    setUserResults([]);
    setUsersLoading(false);
    setUserSearchError(null);
    setStep("configure");
    setPreviewLoading(false);
    setPreview(null);
    setReviewInput(null);
    setSubmitting(false);
  }, [open, countOnly]);

  useEffect(() => {
    const normalizedQuery = userQuery.trim();
    if (!open || draft.scope !== "selected_users" || !normalizedQuery) {
      setUserResults([]);
      setUsersLoading(false);
      setUserSearchError(null);
      return undefined;
    }

    let active = true;
    setUsersLoading(true);
    setUserSearchError(null);
    const timer = window.setTimeout(() => {
      void api
        .users({ page: 1, pageSize: 30, q: normalizedQuery })
        .then((result) => {
          if (active) setUserResults(result.items);
        })
        .catch((error) => {
          if (!active) return;
          setUserResults([]);
          setUserSearchError(
            error instanceof Error ? error.message : "用户搜索失败",
          );
        })
        .finally(() => {
          if (active) setUsersLoading(false);
        });
    }, 220);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [draft.scope, open, userQuery]);

  const updateDraft = (
    update: (current: AutoDormCheckGrantDraft) => AutoDormCheckGrantDraft,
  ) => {
    setDraft(update);
    setPreview(null);
    setReviewInput(null);
  };

  const toggleSelectedUser = (user: UserSummary) => {
    const selected = selectedUsers.some((item) => item.id === user.id);
    const nextUsers = selected
      ? selectedUsers.filter((item) => item.id !== user.id)
      : [...selectedUsers, user];
    setSelectedUsers(nextUsers);
    updateDraft((current) => ({
      ...current,
      selectedUserIds: nextUsers.map((item) => item.id),
    }));
  };

  const review = async () => {
    const validationError = validateAutoDormCheckGrantDraft(draft);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    const input = buildAutoDormCheckGrantInput(draft);
    if (!input || previewLoading) return;

    const sequence = ++previewSequence.current;
    setPreviewLoading(true);
    try {
      const nextPreview = isCourse
        ? await api
            .courseGrabGrantPreview(courseGrabGrantInput(input))
            .then((result) => ({
              ...input,
              includeFuture: result.includeFuture,
              eligibleCount: result.recipientCount,
            }))
        : isLerun
          ? await api.previewLerunGrant(input)
          : await api.previewAutoDormCheckGrant(input);
      if (previewSequence.current !== sequence) return;
      setReviewInput(input);
      setPreview(nextPreview);
      setStep("review");
    } catch (error) {
      if (previewSequence.current === sequence) {
        toast.error(
          error instanceof Error ? error.message : "发放范围核对失败",
        );
      }
    } finally {
      if (previewSequence.current === sequence) setPreviewLoading(false);
    }
  };

  const submit = async () => {
    if (!reviewInput || !preview || submitting) return;
    const fingerprint = autoDormCheckGrantFingerprint(reviewInput);
    const attempt =
      submissionAttempt.current?.fingerprint === fingerprint
        ? submissionAttempt.current
        : {
            fingerprint,
            idempotencyKey: window.crypto.randomUUID(),
          };
    submissionAttempt.current = attempt;
    setSubmitting(true);
    try {
      if (props.product === "course-grab") {
        const result = await api.courseGrabGrant(
          courseGrabGrantInput(reviewInput),
          attempt.idempotencyKey,
        );
        await props.onGranted();
        toast.success(
          result.includeFuture
            ? `已发放给 ${result.recipientCount} 位用户，未来符合条件的用户也会收到`
            : `已发放给 ${result.recipientCount} 位用户`,
        );
      } else if (props.product === "lerun") {
        const campaign = await api.createLerunGrant(
          reviewInput,
          attempt.idempotencyKey,
        );
        await props.onGranted();
        toast.success(
          campaign.includeFuture
            ? `已发放给 ${campaign.initialRecipientCount} 位用户，未来符合条件的用户也会收到`
            : `已发放给 ${campaign.initialRecipientCount} 位用户`,
        );
      } else {
        const campaign = await api.createAutoDormCheckGrant(
          reviewInput,
          attempt.idempotencyKey,
        );
        await props.onGranted(campaign);
        toast.success(
          campaign.includeFuture
            ? `已发放给 ${campaign.initialRecipientCount} 位用户，未来符合条件的用户也会收到`
            : `已发放给 ${campaign.initialRecipientCount} 位用户`,
        );
      }
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "额度发放失败");
    } finally {
      setSubmitting(false);
    }
  };

  const reviewedPaidPlan = reviewInput?.paidPlanCode
    ? planByCode.get(reviewInput.paidPlanCode)
    : undefined;
  const noImmediateRecipient = Boolean(
    preview && preview.eligibleCount === 0 && !preview.includeFuture,
  );

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      className="m-auto max-h-[calc(100vh-24px)] w-[min(760px,calc(100vw-24px))] overflow-hidden rounded-2xl border border-sand-light bg-paper p-0 text-ink shadow-2xl backdrop:bg-ink/35 backdrop:backdrop-blur-sm"
      onCancel={(event) => {
        event.preventDefault();
        if (!busy) onClose();
      }}
      onClick={(event) => {
        if (event.target === dialogRef.current && !busy) onClose();
      }}
    >
      <div className="flex max-h-[calc(100vh-24px)] flex-col">
        <header className="flex flex-none items-start gap-4 border-b border-sand-light/70 px-5 py-5 sm:px-6">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-clay-soft text-clay-deep">
            <Gift size={19} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 id={titleId} className="font-display text-lg font-medium">
                {isLerun
                  ? "发放乐跑次数"
                  : isCourse
                    ? "发放抢课次数"
                    : "发放打卡额度"}
              </h2>
              <Tag tone="clay">
                {step === "configure" ? "第 1 步" : "第 2 步"}
              </Tag>
            </div>
            <p
              id={descriptionId}
              className="mt-1 text-[12px] leading-relaxed text-ink-soft"
            >
              {step === "configure"
                ? countOnly
                  ? "自行设定增加的次数，再选择领取用户。"
                  : "自行设定增加的天数或次数，再选择领取用户。"
                : "核对本次发放范围，确认后立即生效。"}
            </p>
          </div>
          <button
            type="button"
            aria-label="关闭"
            disabled={busy}
            onClick={onClose}
            className="rounded-lg p-2 text-ink-faint transition hover:bg-peach hover:text-ink disabled:opacity-40"
          >
            <X size={18} />
          </button>
        </header>

        {step === "configure" ? (
          <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6">
            <fieldset disabled={busy} className="space-y-6 disabled:opacity-70">
              <section>
                <div className="flex items-end justify-between gap-3">
                  <div>
                    <h3 className="text-[13px] font-medium text-ink">
                      发放额度
                    </h3>
                    <p className="mt-0.5 text-[11.5px] text-ink-faint">
                      {isLerun
                        ? "增加待兑换次数，OpenSZXD 余额用完后自动兑换"
                        : "额度会叠加到用户已有余额，不受售卖套餐限制"}
                    </p>
                  </div>
                  {draft.quotaAmount ? <Tag tone="sage">已填写</Tag> : null}
                </div>
                <div className="mt-3 grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(220px,0.8fr)]">
                  <div
                    className={`grid ${countOnly ? "grid-cols-1" : "grid-cols-2"} gap-2 rounded-xl border border-sand-light bg-cream/45 p-2`}
                    role="radiogroup"
                    aria-label="额度类型"
                  >
                    {[
                      {
                        value: "time" as const,
                        label: "按时间",
                        hint: "连续可用天数",
                        icon: Clock3,
                      },
                      {
                        value: "count" as const,
                        label: "按次数",
                        hint: "不限有效期",
                        icon: Hash,
                      },
                    ]
                      .filter(
                        (option) => !countOnly || option.value === "count",
                      )
                      .map((option) => {
                        const selected = draft.quotaKind === option.value;
                        const Icon = option.icon;
                        return (
                          <button
                            key={option.value}
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            onClick={() =>
                              updateDraft((current) => ({
                                ...current,
                                quotaKind: option.value,
                                quotaAmount: "",
                              }))
                            }
                            className={`flex min-h-20 items-center gap-3 rounded-lg border px-3 py-3 text-left transition ${
                              selected
                                ? "border-clay bg-paper shadow-sm"
                                : "border-transparent text-ink-soft hover:bg-paper/70"
                            }`}
                          >
                            <span
                              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                                selected
                                  ? "bg-clay text-paper"
                                  : "bg-cream-deep text-ink-faint"
                              }`}
                            >
                              <Icon size={16} />
                            </span>
                            <span className="min-w-0">
                              <strong className="block text-[12.5px] font-medium text-ink">
                                {option.label}
                              </strong>
                              <small className="mt-0.5 block text-[10.5px] text-ink-faint">
                                {option.hint}
                              </small>
                            </span>
                          </button>
                        );
                      })}
                  </div>
                  <label className="flex min-h-20 items-center gap-3 rounded-xl border border-sand-light bg-paper px-4 py-3">
                    <span className="min-w-0 flex-1">
                      <span className="block text-[11.5px] font-medium text-ink">
                        {draft.quotaKind === "time" ? "增加天数" : "增加次数"}
                      </span>
                      <span className="mt-0.5 block text-[10.5px] text-ink-faint">
                        {draft.quotaKind === "time"
                          ? "整数，最多 3650 天"
                          : `整数，最多 ${isCourse ? 1000 : 100000} 次`}
                      </span>
                    </span>
                    <span className="flex items-baseline gap-1 rounded-lg border border-sand bg-cream/35 px-3 py-2 focus-within:border-clay">
                      <input
                        type="number"
                        inputMode="numeric"
                        min={1}
                        max={
                          draft.quotaKind === "time"
                            ? 3650
                            : isCourse
                              ? 1000
                              : 100000
                        }
                        step={1}
                        value={draft.quotaAmount}
                        onChange={(event) =>
                          updateDraft((current) => ({
                            ...current,
                            quotaAmount: event.target.value,
                          }))
                        }
                        placeholder="0"
                        className="w-20 bg-transparent text-right font-mono text-[15px] font-medium text-ink outline-none placeholder:text-ink-faint/45"
                      />
                      <span className="text-[11px] text-ink-faint">
                        {draft.quotaKind === "time" ? "天" : "次"}
                      </span>
                    </span>
                  </label>
                </div>
              </section>

              <section>
                <h3 className="text-[13px] font-medium text-ink">发放对象</h3>
                <div
                  className="mt-3 grid gap-2 sm:grid-cols-2"
                  role="radiogroup"
                  aria-label="发放对象"
                >
                  {SCOPE_OPTIONS.filter((option) =>
                    isLerun
                      ? option.value === "all_users" ||
                        option.value === "selected_users"
                      : !isCourse || option.value !== "paid_plan",
                  ).map((option) => {
                    const Icon = option.icon;
                    const selected = draft.scope === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        onClick={() =>
                          updateDraft((current) => ({
                            ...current,
                            scope: option.value,
                          }))
                        }
                        className={`flex items-center gap-3 rounded-xl border p-3 text-left transition active:scale-[0.99] ${
                          selected
                            ? "border-clay bg-clay-soft/65"
                            : "border-sand-light bg-paper hover:border-sand hover:bg-cream/55"
                        }`}
                      >
                        <span
                          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                            selected
                              ? "bg-clay text-paper"
                              : "bg-cream-deep text-ink-soft"
                          }`}
                        >
                          <Icon size={16} />
                        </span>
                        <span className="min-w-0 flex-1">
                          <strong className="block text-[12.5px] font-medium">
                            {option.label}
                          </strong>
                          <small className="mt-0.5 block text-[10.5px] text-ink-faint">
                            {isCourse && option.value === "paid_any"
                              ? "当前仍有有效购买次数的用户"
                              : option.description}
                          </small>
                        </span>
                        {selected ? (
                          <CircleCheck
                            size={16}
                            className="shrink-0 text-clay"
                          />
                        ) : null}
                      </button>
                    );
                  })}
                </div>

                {draft.scope === "selected_users" ? (
                  <UserPicker
                    query={userQuery}
                    onQueryChange={setUserQuery}
                    results={userResults}
                    selectedUsers={selectedUsers}
                    loading={usersLoading}
                    error={userSearchError}
                    onToggle={toggleSelectedUser}
                  />
                ) : null}

                {draft.scope === "paid_plan" ? (
                  <div className="mt-3 rounded-xl border border-sand-light bg-cream/45 p-3 text-[11.5px] text-ink-soft">
                    <p>筛选：购买过的套餐</p>
                    <FilterPopover
                      label="套餐"
                      value={draft.paidPlanCode || ""}
                      options={plans.map((plan) => ({
                        value: plan.code,
                        label: `${plan.label} · ${formatPlanPrice(plan)}`,
                      }))}
                      onChange={(value) =>
                        updateDraft((current) => ({
                          ...current,
                          paidPlanCode:
                            (value as AutoDormCheckGrantDraft["paidPlanCode"]) ||
                            null,
                        }))
                      }
                      columns={2}
                      hideLabel
                      className="mt-1.5 w-full"
                      triggerClassName="w-full justify-between px-3 py-2.5"
                    />
                    <span className="mt-1.5 block text-[10.5px] text-ink-faint">
                      只按成功付款记录筛选，管理员发放不计入付费。
                    </span>
                  </div>
                ) : null}

                {draft.scope === "all_users" ||
                draft.scope === "paid_any" ||
                draft.scope === "paid_plan" ? (
                  <div className="mt-3 rounded-xl border border-sand-light bg-cream/45 p-3">
                    <p className="text-[11.5px] font-medium text-ink">
                      后续领取范围
                    </p>
                    <div
                      className="mt-2 grid grid-cols-2 gap-2"
                      role="radiogroup"
                      aria-label="后续领取范围"
                    >
                      {[
                        { value: false, label: "仅当前符合条件" },
                        { value: true, label: "包含以后首次符合条件" },
                      ].map((option) => (
                        <button
                          key={String(option.value)}
                          type="button"
                          role="radio"
                          aria-checked={draft.includeFuture === option.value}
                          onClick={() =>
                            updateDraft((current) => ({
                              ...current,
                              includeFuture: option.value,
                            }))
                          }
                          className={`rounded-lg border px-3 py-2 text-[11.5px] font-medium transition ${
                            draft.includeFuture === option.value
                              ? "border-clay bg-paper text-clay-deep"
                              : "border-sand-light bg-transparent text-ink-soft hover:border-sand"
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                    <p className="mt-2 text-[10.5px] leading-relaxed text-ink-faint">
                      {isLerun
                        ? "同一发放任务中每位用户最多领取一次。"
                        : "同一发放任务中每位用户最多领取一次，重复购买不会再次领取。"}
                    </p>
                  </div>
                ) : null}
              </section>
            </fieldset>
          </div>
        ) : (
          <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6">
            <div className="rounded-2xl border border-sand-light bg-cream/50 p-4 sm:p-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <ReviewItem
                  label="发放额度"
                  value={
                    reviewInput
                      ? `${reviewInput.quotaAmount} ${reviewInput.quotaKind === "time" ? "天" : "次"}`
                      : "—"
                  }
                  detail={
                    reviewInput?.quotaKind === "time"
                      ? "按校历连续使用，假期暂停并顺延"
                      : isLerun
                        ? "存入待兑换余额，不立即生成 CDK"
                        : isCourse
                          ? "不限有效期，抢课成功后扣减"
                          : "不限有效期，打卡成功后扣减"
                  }
                />
                <ReviewItem
                  label="发放对象"
                  value={reviewInput ? scopeLabel(reviewInput.scope) : "—"}
                  detail={
                    reviewInput?.scope === "selected_users"
                      ? selectedUsers.map((user) => user.displayName).join("、")
                      : reviewInput?.scope === "paid_plan"
                        ? `筛选：${reviewedPaidPlan?.label || reviewInput.paidPlanCode || "—"}`
                        : undefined
                  }
                />
                <ReviewItem
                  label="当前符合条件"
                  value={`${preview?.eligibleCount ?? 0} 位用户`}
                />
                <ReviewItem
                  label="以后符合条件的用户"
                  value={preview?.includeFuture ? "自动发放" : "不发放"}
                />
              </div>
            </div>

            {noImmediateRecipient ? (
              <div className="mt-4 rounded-xl border border-[#E8C9B8] bg-[#F9E9E1] px-4 py-3 text-[12px] leading-relaxed text-destructive">
                当前没有符合条件的用户，且未开启以后自动发放，无法创建空发放任务。
              </div>
            ) : (
              <InfoBox className="mt-4">
                <CircleCheck size={14} className="shrink-0 text-sage" />
                自定义额度会叠加到已有余额；同一发放任务不会向同一用户重复发放。
              </InfoBox>
            )}
          </div>
        )}

        <footer className="flex flex-none flex-wrap items-center justify-end gap-2 border-t border-sand-light/70 bg-cream/35 px-5 py-4 sm:px-6">
          {step === "configure" ? (
            <>
              <ActionButton tone="ghost" disabled={busy} onClick={onClose}>
                取消
              </ActionButton>
              <ActionButton
                tone="dark"
                busy={previewLoading}
                onClick={() => void review()}
              >
                核对发放
                <ArrowRight size={13} />
              </ActionButton>
            </>
          ) : (
            <>
              <ActionButton
                tone="ghost"
                disabled={submitting}
                onClick={() => {
                  setStep("configure");
                  setPreview(null);
                  setReviewInput(null);
                }}
              >
                <ArrowLeft size={13} />
                返回修改
              </ActionButton>
              <ActionButton
                tone="dark"
                busy={submitting}
                disabled={noImmediateRecipient}
                onClick={() => void submit()}
              >
                确认发放
              </ActionButton>
            </>
          )}
        </footer>
      </div>
    </dialog>
  );
}

export function UserPicker<
  T extends Pick<
    UserSummary,
    "id" | "displayName" | "studentNumber" | "majorName" | "account"
  >,
>({
  query,
  onQueryChange,
  results,
  selectedUsers,
  loading,
  error,
  onToggle,
}: {
  query: string;
  onQueryChange: (value: string) => void;
  results: T[];
  selectedUsers: T[];
  loading: boolean;
  error: string | null;
  onToggle: (user: T) => void;
}) {
  return (
    <div className="mt-3 rounded-xl border border-dashed border-sand bg-cream/45 p-3">
      <div className="flex items-center gap-2 rounded-lg border border-sand-light bg-paper px-3 py-2">
        <Search size={13} className="shrink-0 text-ink-faint" />
        <input
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          autoComplete="off"
          placeholder="搜索姓名、学号或账号…"
          className="min-w-0 flex-1 bg-transparent text-[12.5px] outline-none placeholder:text-ink-faint/70"
        />
        {selectedUsers.length ? (
          <Tag tone="clay">已选 {selectedUsers.length} 人</Tag>
        ) : null}
      </div>

      {selectedUsers.length ? (
        <div className="mt-2.5 flex flex-wrap gap-2" aria-label="已选用户">
          {selectedUsers.map((user) => (
            <button
              key={user.id}
              type="button"
              aria-label={`取消选择${user.displayName}`}
              onClick={() => onToggle(user)}
              className="flex items-center gap-1.5 rounded-full border border-clay bg-clay-soft px-2.5 py-1 text-[11.5px] font-medium text-clay-deep"
            >
              {user.displayName} · {user.studentNumber || user.account}
              <X size={11} />
            </button>
          ))}
        </div>
      ) : null}

      <div className="scrollbar-thin mt-2.5 max-h-48 overflow-y-auto">
        {!query.trim() ? (
          <SearchMessage>输入姓名、学号或账号搜索用户</SearchMessage>
        ) : loading ? (
          <SearchMessage>
            <LoaderCircle size={13} className="animate-spin" />
            正在搜索
          </SearchMessage>
        ) : error ? (
          <SearchMessage tone="danger">{error}</SearchMessage>
        ) : results.length ? (
          <div className="divide-y divide-sand-light/65 overflow-hidden rounded-lg border border-sand-light bg-paper">
            {results.map((user) => {
              const selected = selectedUsers.some(
                (item) => item.id === user.id,
              );
              return (
                <button
                  key={user.id}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => onToggle(user)}
                  className={`flex w-full items-center gap-3 px-3 py-2.5 text-left transition ${
                    selected ? "bg-clay-soft/60" : "hover:bg-cream/65"
                  }`}
                >
                  <span
                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-medium ${
                      selected
                        ? "bg-clay text-paper"
                        : "bg-cream-deep text-ink-soft"
                    }`}
                  >
                    {selected ? (
                      <Check size={13} />
                    ) : (
                      user.displayName.slice(0, 1)
                    )}
                  </span>
                  <span className="min-w-0 flex-1">
                    <strong className="block truncate text-[12px] font-medium text-ink">
                      {user.displayName}
                    </strong>
                    <small className="mt-0.5 block truncate text-[10.5px] text-ink-faint">
                      {user.studentNumber || user.account}
                      {user.studentNumber && user.account !== user.studentNumber
                        ? ` · 账号 ${user.account}`
                        : ""}
                      {user.majorName ? ` · ${user.majorName}` : ""}
                    </small>
                  </span>
                  {selected ? <Tag tone="sage">已选</Tag> : null}
                </button>
              );
            })}
          </div>
        ) : (
          <SearchMessage>没有匹配用户</SearchMessage>
        )}
      </div>
    </div>
  );
}

function SearchMessage({
  children,
  tone = "neutral",
}: {
  children: React.ReactNode;
  tone?: "neutral" | "danger";
}) {
  return (
    <p
      className={`flex items-center justify-center gap-1.5 py-4 text-center text-[11.5px] ${
        tone === "danger" ? "text-destructive" : "text-ink-faint"
      }`}
    >
      {children}
    </p>
  );
}

function InfoBox({
  children,
  className = "mt-3",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`flex items-start gap-2 rounded-xl border border-sand-light bg-[#F3F5EF] px-3.5 py-3 text-[11.5px] leading-relaxed text-ink-soft ${className}`}
    >
      {children}
    </div>
  );
}

function ReviewItem({
  label,
  value,
  detail,
}: {
  label: string;
  value: string;
  detail?: string;
}) {
  return (
    <div className="min-w-0">
      <p className="text-[10.5px] text-ink-faint">{label}</p>
      <p className="mt-1 break-words text-[13px] font-medium text-ink">
        {value}
      </p>
      {detail ? (
        <p className="mt-0.5 break-words text-[10.5px] text-ink-faint">
          {detail}
        </p>
      ) : null}
    </div>
  );
}

function formatPlanPrice(plan: AutoDormCheckPlan): string {
  return `¥${plan.priceCents / 100}`;
}

function scopeLabel(scope: AutoDormCheckGrantScope): string {
  return SCOPE_OPTIONS.find((option) => option.value === scope)?.label || scope;
}
